import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { constantsData, statusTypesData } from "@/data";
import { editTrackMutation, getTrackQuery } from "@/graph";
import { openAlert, toggleTrackDrawer } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useEditTrack = (trackFileId = null, id, setId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [editTrackAction, { loading }] = useMutation(editTrackMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getTrackQuery,
                variables: {
                    where: { id },
                },
            },
        ],
    });

    const editTrack = async ({
        customId,
        imageFile,
        isrc,
        iswc,
        nameAr,
        nameEn,
        number,
        otherIsrc,
        primaryArtist,
        secondaryArtists,
        trackContributors,
        trackFile,
        trackFormat,
    }) => {
        try {
            await editTrackAction({
                onCompleted: () => {
                    dispatch(toggleTrackDrawer(false));

                    setId(null);

                    dispatch(
                        openAlert(
                            t("messages.editMsgs.success", { entity: t("tracks.entity") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    data: {
                        artist: primaryArtist && {
                            connect: { id: primaryArtist },
                        },
                        ...(imageFile && {
                            image: {
                                connect: { id: imageFile },
                            },
                        }),
                        isrc: { set: isrc },
                        trackFormat: { connect: { id: trackFormat } },
                        custom_id: { set: customId },
                        isrc_other: otherIsrc ? { set: otherIsrc } : null,
                        iswc: iswc ? { set: iswc } : null,
                        trackContributors,
                        secondary_artists: {
                            set: secondaryArtists?.map(({ id: secondaryArtistId, value }) => ({
                                id: secondaryArtistId || value,
                            })),
                        },
                        title: nameEn && { set: nameEn },
                        title_ar: nameAr && { set: nameAr },
                        ...(trackFileId !== trackFile && {
                            trackFile: trackFile && {
                                connect: { id: trackFile },
                            },
                        }),
                        track_num: number && { set: number && parseInt(number) },
                    },
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        }
    };

    return {
        editTrack,
        loading,
    };
};

export default useEditTrack;
